import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProducts, Product } from '@/lib/api';
import { useCart } from '@/contexts/CartContext';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=Cute%20pastel%20colored%20lifestyle%20accessory%20product%20on%20soft%20cream%20background%2C%20gentle%20shadows%2C%20feminine%20editorial%20product%20photography%2C%20pink%20and%20sage%20tones%2C%20kawaii%20aesthetic&width=600&height=600&seq=roff-detail-fallback-girly&orientation=squarish';

export default function ProductDetailPage() {
  const { id: slug } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [sizeError, setSizeError] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    if (!slug) return;
    fetchProducts()
      .then(products => {
        setProduct(products.find(p => p.slug === slug) ?? null);
        setActiveImage(0);
        setSelectedSize('');
        setSizeError(false);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-foreground-300">
            <i className="ri-loader-4-line text-3xl animate-spin"></i>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <p className="text-foreground-500" style={{ fontFamily: "var(--font-body)" }}>商品が見つかりませんでした</p>
          <Link to="/products" className="text-sm text-foreground-400 hover:text-primary-500 tracking-[0.15em] underline cursor-pointer" style={{ fontFamily: "var(--font-heading)" }}>
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (product.sizeOptions.length > 0 && !selectedSize) {
      setSizeError(true);
      return;
    }
    addItem(product, quantity, selectedSize || undefined);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const images = product.images.length > 0 ? product.images : [product.imageUrl ?? FALLBACK_IMAGE];
  const imageUrl = images[activeImage] ?? images[0];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-10" style={{ fontFamily: "var(--font-body)" }}>
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-primary-500 transition-colors cursor-pointer">Products</Link>
            <span>/</span>
            <span className="text-foreground-700">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-4">
                <img
                  src={imageUrl}
                  alt={product.name}
                  className="w-full h-full object-contain object-center"
                />
              </div>
              {images.length > 1 && (
                <div className="flex gap-3">
                  {images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setActiveImage(i)}
                      className={`w-16 h-16 rounded-lg overflow-hidden bg-background-100 cursor-pointer border-2 transition-colors ${
                        i === activeImage ? 'border-primary-400' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-contain object-center" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <h1 className="text-3xl md:text-4xl font-light text-foreground-950 mb-5 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}>
                {product.name}
              </h1>

              <div className="flex items-center gap-3 mb-8">
                <span className="text-3xl text-foreground-950"
                  style={{ fontFamily: "var(--font-heading)" }}>
                  ¥{product.price.toLocaleString()}
                </span>
              </div>

              {product.description && (
                <div className="text-sm text-foreground-600 leading-relaxed mb-8 whitespace-pre-line"
                  style={{ fontFamily: "var(--font-body)" }}>
                  {product.description}
                </div>
              )}

              {product.size && (
                <div className="mb-8">
                  <p className="text-xs text-foreground-700 tracking-[0.2em] mb-2 uppercase"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    Size
                  </p>
                  <p className="text-sm text-foreground-600" style={{ fontFamily: "var(--font-body)" }}>
                    {product.size}
                  </p>
                </div>
              )}

              {product.sizeOptions.length > 0 && (
                <div className="mb-8">
                  <p className="text-xs text-foreground-700 tracking-[0.2em] mb-3 uppercase"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    サイズを選択 <span className="text-primary-500">*</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.sizeOptions.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => { setSelectedSize(opt); setSizeError(false); }}
                        className={`px-5 py-2 text-sm rounded-full border transition-colors cursor-pointer ${
                          selectedSize === opt
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'border-background-300 text-foreground-700 hover:border-primary-400'
                        }`}
                        style={{ fontFamily: "var(--font-heading)" }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {sizeError && (
                    <p className="text-xs text-primary-600 mt-2">サイズを選択してください</p>
                  )}
                </div>
              )}

              <div className="mb-8">
                <p className="text-xs text-foreground-700 tracking-[0.2em] mb-3 uppercase"
                  style={{ fontFamily: "var(--font-heading)" }}>
                  Quantity
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center border border-background-300 hover:border-primary-400 transition-colors cursor-pointer rounded-full"
                  >
                    <i className="ri-subtract-line text-foreground-700"></i>
                  </button>
                  <span className="w-10 text-center text-base text-foreground-950"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                    className="w-10 h-10 flex items-center justify-center border border-background-300 hover:border-primary-400 transition-colors cursor-pointer rounded-full"
                  >
                    <i className="ri-add-line text-foreground-700"></i>
                  </button>
                  <span className="text-sm text-foreground-500 ml-2" style={{ fontFamily: "var(--font-body)" }}>
                    在庫: {product.stock}点
                  </span>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className={`w-full py-4 text-sm tracking-[0.2em] transition-all whitespace-nowrap cursor-pointer rounded-full ${
                  added
                    ? 'bg-accent-500 text-white'
                    : product.stock === 0
                    ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {added ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-check-line"></i> Added to Cart
                  </span>
                ) : product.stock === 0 ? (
                  'Sold Out'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-shopping-bag-line"></i> Add to Cart
                  </span>
                )}
              </button>

              <div className="mt-8 pt-8 border-t border-background-200 flex justify-center gap-4">
                {[
                  { icon: 'ri-customer-service-2-line', label: 'Support', sub: '24時間対応' },
                ].map(item => (
                  <div key={item.label} className="text-center">
                    <div className="w-9 h-9 flex items-center justify-center mx-auto mb-2 text-primary-500">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <p className="text-[10px] text-foreground-800" style={{ fontFamily: "var(--font-heading)" }}>{item.label}</p>
                    <p className="text-[10px] text-foreground-500" style={{ fontFamily: "var(--font-body)" }}>{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
